// src/screens/TeamPlayers.tsx
import { useForm, SubmitHandler } from "react-hook-form";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "../supabaseClient";
import { sanitizeFileName } from "../utils/storage";
import i18n from "../i18n";

type Player = {
  id: string;
  firstName: string;
  lastName: string;
  number: number;
  position: string;
  photoUrl?: string;
};

type TeamRow = {
  id: string;
  user_id: string;
  team_name: string;
  league?: string | null;
  players: Player[] | null;
};

type PlayerForm = Omit<Player, "id" | "photoUrl">;

/* ===== i18n mini-hook ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

export const TeamPlayers = () => {
  const { t} = useI18nSafe();
  const { teamId } = useParams();
  const { register, handleSubmit, reset } = useForm<PlayerForm>();
  const [team, setTeam] = useState<TeamRow | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !teamId) {
        setLoading(false);
        return;
      }

      // On sécurise : on ne charge que si l’équipe appartient à l’utilisateur
      const { data, error } = await supabase
        .from("teams")
        .select("id, user_id, team_name, league, players")
        .eq("id", teamId)
        .eq("user_id", user.id)
        .single();

      if (error || !data) {
        console.error(error);
        setLoading(false);
        return;
      }

      setTeam(data as TeamRow);
      setPlayers((data.players || []) as Player[]);
      setLoading(false);
    };
    load();
  }, [teamId]);

  const onSubmit: SubmitHandler<PlayerForm> = async (data) => {
    if (!team) return;

    // empêcher doublon de numéro
    const numberAlreadyUsed = (players || []).some((p) => p.number === data.number);
    if (numberAlreadyUsed) {
      alert(t("teamplayers:alerts.number_exists"));
      return;
    }

    // Upload éventuel de la photo
    let photoUrl = "";
    if (photoFile) {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) return;

      const fileName = `${Date.now()}-${sanitizeFileName(photoFile.name)}`;
      const filePath = `players/${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("player-photos")
        .upload(filePath, photoFile, { cacheControl: "3600", upsert: true });

      if (uploadError) {
        console.error(uploadError);
        alert(t("teamplayers:errors.photo_upload"));
        return;
      }
      photoUrl = filePath;
    }

    const newPlayer: Player = {
      id: uuidv4(),
      firstName: data.firstName,
      lastName: data.lastName,
      number: data.number,
      position: data.position,
      photoUrl,
    };

    const updatedPlayers = [...players, newPlayer];

    const { error: updateError } = await supabase
      .from("teams")
      .update({ players: updatedPlayers })
      .eq("id", team.id);

    if (updateError) {
      console.error(updateError);
      alert(t("teamplayers:errors.add_player"));
      return;
    }

    setPlayers(updatedPlayers);
    setPhotoFile(null);
    reset();
  };

  if (loading) {
    return (
      <section className="p-6 min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
        {t("common:loading")}
      </section>
    );
  }

  if (!team) {
    return (
      <section className="p-6 min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
        <p className="mb-4">{t("teamplayers:errors.team_not_found")}</p>
        <Link to="/teams/new" className="text-orange-600 hover:underline">
          {t("teamplayers:links.create_team")}
        </Link>
      </section>
    );
  }

  return (
    <section className="flex flex-col items-center p-6 min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      <h1 className="text-2xl font-bold text-orange-600 dark:text-orange-400 mb-2">
        {team.team_name}
      </h1>
      {team.league && (
        <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t("teamplayers:labels.league")} : {team.league}
        </div>
      )}

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full max-w-md space-y-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-sm"
      >
        <input
          {...register("firstName", { required: true })}
          placeholder={t("teamplayers:placeholders.first_name")}
          className="w-full border border-gray-300 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
        />
        <input
          {...register("lastName", { required: true })}
          placeholder={t("teamplayers:placeholders.last_name")}
          className="w-full border border-gray-300 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
        />
        <input
          {...register("number", { required: true, valueAsNumber: true })}
          type="number"
          placeholder={t("teamplayers:placeholders.number")}
          className="w-full border border-gray-300 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
        />
        <input
          {...register("position", { required: true })}
          placeholder={t("teamplayers:placeholders.position")}
          className="w-full border border-gray-300 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
        />

        <label className="block">
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t("teamplayers:labels.photo")}
          </span>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                setPhotoFile(e.target.files[0]);
              }
            }}
            className="w-full border border-gray-300 dark:border-gray-700 rounded-lg p-2 bg-white dark:bg-gray-900"
          />
          {photoFile && (
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
              {photoFile.name} — {(photoFile.size / 1024).toFixed(1)} {t("teamplayers:kb_suffix")}
            </p>
          )}
        </label>

        <button
          type="submit"
          className="bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
        >
          {t("teamplayers:buttons.add_player")}
        </button>
      </form>

      <div className="mt-8 w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4">{t("teamplayers:labels.players")}</h2>
        {players.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">{t("teamplayers:labels.no_players")}</p>
        ) : (
          <ul className="space-y-2">
            {players.map((p) => (
              <li
                key={p.id}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 rounded-xl shadow-sm"
              >
                <div className="text-gray-900 dark:text-gray-100">
                  {p.number} — {p.firstName} {p.lastName} ({p.position})
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-8">
        <Link to="/teams/new" className="text-sm text-orange-600 hover:underline">
          {t("teamplayers:links.create_another_team")}
        </Link>
      </div>
    </section>
  );
};
export {};
